import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, 
  CheckCircle, 
  Clock,
  Shield,
  Info
} from 'lucide-react';
import axios from '../../../utils/axiosConfig';
import { formatDistanceToNow } from 'date-fns';

const WarningsTab = () => {
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadWarnings();
  }, []);

  const loadWarnings = async () => {
    try {
      const response = await axios.get('/settings/warnings');
      setWarnings(response.data?.data || response.data || []);
    } catch (error) {
      console.error('Failed to load warnings:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (warningId) => {
    try {
      await axios.patch(`/settings/warnings/${warningId}/read`);
      setWarnings(warnings.map(w => 
        w.id === warningId ? { ...w, read: true, readAt: new Date().toISOString() } : w
      ));
    } catch (error) {
      console.error('Failed to mark warning as read:', error);
    }
  };

  const formatDate = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (error) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8 sm:py-12">
        <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (warnings.length === 0) {
    return (
      <div className="text-center py-8 sm:py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-xl bg-green-50 shadow-neomorph-inset mb-3 sm:mb-4">
          <Shield className="h-8 w-8 sm:h-10 sm:w-10 text-green-600" />
        </div>
        <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-1 sm:mb-2">No Warnings</h3>
        <p className="text-xs sm:text-sm text-gray-500 max-w-sm mx-auto px-3">
          Your account is in good standing. You have no warnings or violations.
        </p>
      </div>
    );
  }

  const unreadCount = warnings.filter(w => !w.read).length;

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center">
        <div className="p-1.5 sm:p-2 bg-yellow-50 rounded-lg mr-2 sm:mr-3">
          <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-600" />
        </div>
        <div>
          <h3 className="text-sm sm:text-lg font-semibold text-gray-800">Account Warnings</h3>
          <p className="text-[11px] sm:text-sm text-gray-500">
            {unreadCount > 0 
              ? `You have ${unreadCount} unread warning${unreadCount > 1 ? 's' : ''}`
              : 'All warnings have been reviewed'
            }
          </p>
        </div>
      </div>

      <div className="space-y-3 sm:space-y-4">
        {warnings.map((warning) => (
          <div
            key={warning.id}
            className={`rounded-xl p-3 sm:p-5 transition-all border ${
              warning.read 
                ? 'bg-white shadow-neomorph border-primary-100/30' 
                : 'bg-yellow-50/50 shadow-neomorph border-yellow-200'
            }`}
          >
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
              <div className="flex items-start space-x-2 sm:space-x-3 min-w-0 flex-1">
                <div className={`p-1.5 sm:p-2 rounded-lg flex-shrink-0 ${
                  warning.read ? 'bg-gray-100' : 'bg-yellow-100'
                }`}>
                  <AlertTriangle className={`h-4 w-4 sm:h-5 sm:w-5 ${
                    warning.read ? 'text-gray-600' : 'text-yellow-700'
                  }`} />
                </div>
                
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1">
                    <h4 className="font-semibold text-gray-800 text-sm sm:text-base">
                      Community Guidelines Warning
                    </h4>
                    {!warning.read && (
                      <span className="px-1.5 sm:px-2 py-0.5 text-[9px] sm:text-xs font-medium bg-yellow-200 text-yellow-800 rounded-full whitespace-nowrap">
                        New
                      </span>
                    )}
                  </div>
                  
                  <p className="text-xs sm:text-sm text-gray-700 mb-2 break-words">{warning.reason}</p>
                  
                  <div className="flex flex-wrap items-center gap-x-3 sm:gap-x-4 gap-y-1 text-[10px] sm:text-xs">
                    <span className="flex items-center text-gray-500">
                      <Clock className="h-3 w-3 mr-1" />
                      {formatDate(warning.createdAt)}
                    </span>
                    
                    {warning.read && warning.readAt && (
                      <span className="flex items-center text-green-600">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Read {formatDate(warning.readAt)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {!warning.read && (
                <button
                  onClick={() => markAsRead(warning.id)}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-primary-600 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all border border-primary-100 mt-2 sm:mt-0 self-start sm:self-center"
                >
                  Mark as Read
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Warning Information */}
      <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-blue-50 rounded-xl shadow-neomorph-inset border border-blue-200">
        <div className="flex items-start">
          <div className="p-1 sm:p-1.5 bg-blue-100 rounded-lg mr-2 sm:mr-3 flex-shrink-0">
            <Info className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-600" />
          </div>
          <div className="min-w-0">
            <h4 className="text-xs sm:text-sm font-medium text-blue-800 mb-1 sm:mb-1.5">About Warnings</h4>
            <ul className="text-[10px] sm:text-sm text-blue-700 space-y-0.5 sm:space-y-1">
              <li className="break-words">• Warnings are issued for violations of community guidelines</li>
              <li className="break-words">• Multiple warnings may result in account restrictions</li>
              <li className="break-words">• Warnings are reviewed by our moderation team</li>
              <li className="break-words">• You can mark warnings as read after reviewing them</li>
            </ul>
          </div>
        </div>
      </div>

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default WarningsTab;